import React, { useState, useEffect } from 'react';

import { isEmbedded } from '../store/embedded';

const SafariStorageEmbeddedOverlay = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [needsAccess, setNeedsAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // Check if we need Safari storage access
  const checkIfNeedsAccess = () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIframe = window !== window.top;
    return isSafari && isIframe && isEmbedded;
  };

  useEffect(() => {
    const checkStorageAccess = async () => {
      if (!checkIfNeedsAccess()) {
        setHasAccess(true);
        return;
      }

      try {
        if (document.hasStorageAccess) {
          const hasAccess = await document.hasStorageAccess();
          if (hasAccess) {
            setHasAccess(true);
            return;
          }
        }
        setNeedsAccess(true);
      } catch (error) {
        console.warn('Could not check storage access:', error);
        setNeedsAccess(true);
      }
    };

    checkStorageAccess();
  }, []);

  const handleEnableAccess = async () => {
    setIsLoading(true);

    try {
      if (document.requestStorageAccess) {
        await document.requestStorageAccess();
        setHasAccess(true);
        setNeedsAccess(false);
      } else {
        // Continue anyway for non-Safari browsers
        setHasAccess(true);
        setNeedsAccess(false);
      }
    } catch (error) {
      console.error('Storage access denied:', error);
      // You might want to show an error message or allow continued usage
      setHasAccess(true);
      setNeedsAccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Show overlay if we need access and don't have it

  const modal =
    needsAccess && !hasAccess ? (
      <div
        style={{
          zIndex: 1000,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(10, 10, 10, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center"
          style={{
            padding: 20,
            borderRadius: 20,
            background: 'white',
            maxWidth: 1200,
            height: 'auto',
            marginLeft: 20,
            marginRight: 20,
          }}
        >
          {/* Icon */}
          <div className="mb-6">
            <div
              className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto"
              style={{ width: 16, height: 16 }}
            >
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Enable Full Functionality
          </h2>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            To ensure all features work properly in this embedded page, we need
            to request permission to store necessary data. Click the button
            below to continue.
          </p>

          {/* Action Button */}
          <button
            onClick={handleEnableAccess}
            disabled={isLoading}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors duration-200 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Requesting Access...
              </div>
            ) : (
              'Continue to App'
            )}
          </button>

          {/* Help Text */}
          <p className="text-xs text-gray-500 mt-4">
            This is required for Safari's privacy protection in embedded
            content.
          </p>
        </div>
      </div>
    ) : null;

  // Render children when access is granted or not needed
  return (
    <>
      {modal}
      {children}
    </>
  );
};
export default SafariStorageEmbeddedOverlay;
